import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** Rendered in the top-right of the header — usually a save action. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The modal the add-expense form lives in.
 *
 * Three things here are not decoration:
 *
 * - Escape closes it, and focus moves into the sheet on open and back to
 *   whatever opened it on close. Without that, a keyboard user tabs straight
 *   past the dialog into the page behind it.
 * - The body gets overflow:hidden while it is open, otherwise iOS Safari
 *   scrolls the page underneath when you reach the end of the sheet.
 * - Heights are in dvh, not vh. On mobile Safari, vh is the height of the
 *   viewport with the address bar hidden, so a 82vh sheet is taller than the
 *   screen until you scroll — and the save button sits under the chrome.
 */
export function Sheet({ open, title, onClose, action, children }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;

    openerRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      // Deliberately not dismissable by tapping the backdrop. The only thing
      // this sheet holds is a half-filled expense form, and on a phone the
      // backdrop is a thin strip either side of it — easy to catch with a
      // thumb, and there is no undo for the six fields it would discard.
      // Escape and the Cancel button both close it.
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'var(--overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'calc(var(--sp-6) + env(safe-area-inset-top)) var(--sp-3) calc(var(--sp-6) + env(safe-area-inset-bottom))',
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '86dvh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          borderRadius: 22,
          overflow: 'hidden',
          boxShadow: '0 12px 48px rgba(16,42,36,0.28)',
          outline: 'none',
        }}
      >
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--sp-3)',
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: 'var(--fs-body-lg)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Cancel
          </button>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-h3)', fontWeight: 600 }}>
            {title}
          </span>
          <div style={{ minWidth: 52, textAlign: 'right' }}>{action}</div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: 'var(--sp-5)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
